/**
 * Prismic Image CDN optimization helpers
 *
 * Prismic serves images through Imgix. Adding `auto=compress,format` and a sensible width
 * drastically reduces bandwidth without changing your content model.
 */

const isPrismicImageUrl = (url = '') => {
  try {
    const u = new URL(url)
    return u.hostname.includes('images.prismic.io')
  } catch (e) {
    return false
  }
}

const isMobileViewport = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(max-width: 767px)').matches

const mobileWidth = (desktopWidth, mobileWidthValue) =>
  isMobileViewport() ? mobileWidthValue : desktopWidth

export const optimizePrismicImage = (url = '', opts = {}) => {
  if (!url || !isPrismicImageUrl(url)) return url

  const { w, q, fit = 'max' } = opts || {}

  try {
    const u = new URL(url)

    // Always request modern formats + compression
    const auto = u.searchParams.get('auto')
    if (!auto) {
      u.searchParams.set('auto', 'compress,format')
    } else if (!auto.includes('format') || !auto.includes('compress')) {
      const parts = new Set(auto.split(',').map((p) => p.trim()).filter(Boolean))
      parts.add('compress')
      parts.add('format')
      u.searchParams.set('auto', Array.from(parts).join(','))
    }

    // Apply width only if provided and not already set
    if (w && !u.searchParams.get('w')) {
      u.searchParams.set('w', String(w))
    }

    // Optional quality; set only if you choose
    if (q && !u.searchParams.get('q')) {
      u.searchParams.set('q', String(q))
    }

    // Fit helps prevent accidental oversized crops
    if (fit && !u.searchParams.get('fit')) {
      u.searchParams.set('fit', fit)
    }

    return u.toString()
  } catch (e) {
    return url
  }
}

// Convenience presets used across the site
export const prismicImg = {
  bg: (url) => optimizePrismicImage(url, { w: mobileWidth(2400, 1200), q: 74 }),
  hero: (url) => optimizePrismicImage(url, { w: mobileWidth(2000, 1100), q: 76 }),
  grid: (url) => optimizePrismicImage(url, { w: mobileWidth(1400, 900), q: 76 }),
  carousel: (url) => optimizePrismicImage(url, { w: mobileWidth(1600, 900), q: 76 }),
  meta: (url) => optimizePrismicImage(url, { w: 1200, q: 80 }),
  thumb: (url) => optimizePrismicImage(url, { w: mobileWidth(800, 520), q: 72 }),
}
